import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { graphBuilderApi } from '../../../services/api';
import { hasRoles, getCurrentUser } from '../../../utils/roleCheck';
import PackTreeView from './PackTreeView';
import './PackManagementPage.css';

const TABS = [
  { id: 'packs', label: 'Packs' },
  { id: 'registry', label: 'Registry' },
  { id: 'policy', label: 'Decision policy' },
];

function PackManagementPage() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isAdmin = hasRoles(user?.roles, ['CerebralAdmin', 'CompanyAdmin']);

  const [activeTab, setActiveTab] = useState('packs');
  const [packs, setPacks] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Upload + validation state
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingText, setPendingText] = useState('');
  const [validation, setValidation] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [overwrite, setOverwrite] = useState(false);

  const [expandedPackId, setExpandedPackId] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/user-home');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    graphBuilderApi.listPacks()
      .then((data) => {
        if (cancelled) return;
        setPacks(Array.isArray(data?.packs) ? data.packs : []);
        setRegistry(data?.registry || null);
        setPolicy(data?.policy || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err?.message || 'Failed to load packs');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAdmin, navigate, refreshKey]);

  const refreshAll = useCallback(() => setRefreshKey((k) => k + 1), []);

  // ---- Upload / validation handlers ----
  const onFilePicked = useCallback(async (file) => {
    if (!file) return;
    setSaveError(null);
    setSaveSuccess(null);
    setPendingFile(file);
    try {
      const text = await file.text();
      setPendingText(text);
    } catch (err) {
      console.error('failed to read file', err);
    }
    setUploading(true);
    setValidation(null);
    try {
      const v = await graphBuilderApi.validatePack(file);
      setValidation(v);
    } catch (err) {
      setValidation({
        ok: false,
        kind: null,
        parsed: null,
        errors: [{ severity: 'error', code: 'http_error',
          message: err?.response?.data?.detail || err?.message }],
        warnings: [],
        case_literals_found: [],
      });
    } finally {
      setUploading(false);
    }
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) onFilePicked(file);
  }, [onFilePicked]);

  const onDragOver = useCallback((e) => e.preventDefault(), []);

  const handleSave = useCallback(async () => {
    if (!validation?.ok || !pendingText || !validation.kind) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const result = await graphBuilderApi.savePack(
        pendingText,
        validation.kind,
        { overwrite, registerInRegistry: validation.kind === 'pack' && !overwrite },
      );
      if (!result?.ok) {
        setSaveError(
          result?.validation?.errors?.[0]?.message
          || 'Save failed; see validation details above.',
        );
      } else {
        setSaveSuccess(`Saved to ${result.saved_path}` + (result.registry_updated ? ' (registry updated)' : ''));
        setPendingFile(null);
        setPendingText('');
        setValidation(null);
        refreshAll();
      }
    } catch (err) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [validation, pendingText, overwrite, refreshAll]);

  const filteredPacks = useMemo(() => packs, [packs]);

  if (!isAdmin) return null;

  return (
    <div className="pack-mgmt-page">
      <div className="pack-mgmt-header">
        <div>
          <h1>Category Pack Management</h1>
          <p className="pack-mgmt-subtitle">
            Upload, validate, and visualise the YAML files that drive the
            categorical deep-chain reasoner. Editing is read-only in this phase
            — uploads append to disk after passing validation.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={refreshAll}
        >
          ↻ Refresh
        </button>
      </div>

      <div className="pack-mgmt-upload">
        <div
          className={`pack-mgmt-dropzone ${pendingFile ? 'with-file' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <strong>Upload YAML</strong>
          <p>Drop a <code>.yaml</code> here, or click to browse. Pack / registry / policy autodetected.</p>
          <input
            type="file"
            accept=".yaml,.yml,application/x-yaml,text/yaml"
            onChange={(e) => onFilePicked(e.target.files?.[0])}
          />
          {pendingFile && (
            <div className="pack-mgmt-pending">
              <span>{pendingFile.name}</span>
              <button
                type="button"
                className="btn-link"
                onClick={() => { setPendingFile(null); setPendingText(''); setValidation(null); setSaveError(null); setSaveSuccess(null); }}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {(uploading || validation) && (
          <div className="pack-mgmt-validation">
            <h3>Validation result</h3>
            {uploading && <p>Validating…</p>}
            {validation && (
              <ValidationPanel validation={validation} />
            )}
            {validation?.ok && (
              <div className="pack-mgmt-save-row">
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={overwrite}
                    onChange={(e) => setOverwrite(e.target.checked)}
                  />
                  Overwrite if exists
                </label>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving…' : 'Save to disk'}
                </button>
              </div>
            )}
            {saveError && <p className="pack-mgmt-error">{saveError}</p>}
            {saveSuccess && <p className="pack-mgmt-success">{saveSuccess}</p>}
          </div>
        )}
      </div>

      <div className="pack-mgmt-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            className={`pack-mgmt-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="pack-mgmt-body">
        {loading && <p>Loading…</p>}
        {error && <p className="pack-mgmt-error">{error}</p>}

        {!loading && !error && activeTab === 'packs' && (
          <PacksTab
            packs={filteredPacks}
            expandedPackId={expandedPackId}
            onToggle={(id) => setExpandedPackId((cur) => cur === id ? null : id)}
          />
        )}

        {!loading && !error && activeTab === 'registry' && (
          <RegistryTab registry={registry} />
        )}

        {!loading && !error && activeTab === 'policy' && (
          <PolicyTab policy={policy} />
        )}
      </div>
    </div>
  );
}

// ---- ValidationPanel -------------------------------------------------------
function ValidationPanel({ validation }) {
  const { ok, kind, errors = [], warnings = [], case_literals_found = [] } = validation || {};
  const status = ok ? 'green' : (errors.length ? 'red' : 'amber');
  return (
    <div className={`validation-panel validation-${status}`}>
      <div className="validation-header">
        <span className={`validation-pill validation-${status}`}>
          {ok ? 'OK' : (errors.length ? 'ERROR' : 'WARNING')}
        </span>
        <span>Detected kind: <strong>{kind || 'unknown'}</strong></span>
      </div>
      {case_literals_found.length > 0 && (
        <div className="validation-section">
          <strong>Case literals found:</strong>
          <ul>
            {case_literals_found.map((l, i) => <li key={i}><code>{l}</code></li>)}
          </ul>
        </div>
      )}
      {errors.length > 0 && (
        <div className="validation-section">
          <strong>Errors</strong>
          <ul>
            {errors.map((e, i) => (
              <li key={i}>
                <code>{e.code}</code> — {e.message}
                {e.path && <span className="muted"> ({e.path})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="validation-section">
          <strong>Warnings</strong>
          <ul>
            {warnings.map((w, i) => (
              <li key={i}>
                <code>{w.code}</code> — {w.message}
                {w.path && <span className="muted"> ({w.path})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---- PacksTab --------------------------------------------------------------
function PacksTab({ packs, expandedPackId, onToggle }) {
  if (!packs.length) {
    return <p className="muted">No packs found in the category_packs/ directory.</p>;
  }
  return (
    <table className="pack-mgmt-table">
      <thead>
        <tr>
          <th>pack_id</th>
          <th>name</th>
          <th>domain</th>
          <th>severity</th>
          <th>chains</th>
          <th>last modified</th>
        </tr>
      </thead>
      <tbody>
        {packs.map((p) => (
          <React.Fragment key={p.pack_id}>
            <tr
              className={`pack-row ${expandedPackId === p.pack_id ? 'expanded' : ''}`}
              onClick={() => onToggle(p.pack_id)}
            >
              <td><code>{p.pack_id}</code></td>
              <td>{p.name || '—'}</td>
              <td>{p.domain || '—'}</td>
              <td>
                <span className={`severity-badge sev-${(p.severity || '').toLowerCase()}`}>
                  {p.severity || 'UNKNOWN'}
                </span>
              </td>
              <td>{p.n_chains}</td>
              <td className="muted">{p.last_modified || '—'}</td>
            </tr>
            {expandedPackId === p.pack_id && (
              <tr className="pack-row-detail">
                <td colSpan={6}>
                  <PackTreeView pack={p} />
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

// ---- RegistryTab -----------------------------------------------------------
function RegistryTab({ registry }) {
  if (!registry) return <p className="muted">No pack_registry.yaml found.</p>;
  const domains = registry.domains || {};
  return (
    <div className="registry-tab">
      <p className="muted">Source: <code>{registry.file}</code></p>
      {Object.entries(domains).map(([dom, packIds]) => (
        <div key={dom} className="registry-domain">
          <h3>{dom}</h3>
          {packIds.length === 0 ? (
            <p className="muted">— empty —</p>
          ) : (
            <ul>
              {packIds.map((pid) => <li key={pid}><code>{pid}</code></li>)}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- PolicyTab -------------------------------------------------------------
function PolicyTab({ policy }) {
  if (!policy) return <p className="muted">No decision_policy.yaml found.</p>;
  const weights = policy.severity_weights || {};
  const rules = policy.rules || [];
  return (
    <div className="policy-tab">
      <p className="muted">Source: <code>{policy.file}</code></p>
      <h3>Severity weights</h3>
      <table className="pack-mgmt-table small">
        <thead>
          <tr><th>severity</th><th>weight</th></tr>
        </thead>
        <tbody>
          {Object.entries(weights).map(([s, w]) => (
            <tr key={s}>
              <td>{s}</td>
              <td>{w}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Rules (first match wins)</h3>
      <table className="pack-mgmt-table">
        <thead>
          <tr><th>#</th><th>condition</th><th>then / else</th></tr>
        </thead>
        <tbody>
          {rules.map((r, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td><code>{r.if || (r.else !== undefined ? '— else —' : JSON.stringify(r))}</code></td>
              <td>{r.then || r.else || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PackManagementPage;
